import Util from './lib/util.js'
import 'what-input'

const LT_Theme = (
	() => {
		'use strict'

		const publicAPIs = {}

		publicAPIs.init = () => {
			if ( document.readyState === "interactive" || document.readyState === "complete" ) {
				constructor();
			} else {
				document.addEventListener( "DOMContentLoaded", constructor );
			}
		}

		const constructor = () => {
			// Click-to-toggle elements (https://codyhouse.co/ds/components/info/collapse)
			initCollapse();

			// Custom "buttons-toggle" block that allows toggling the height of a button group
			initButtonsToggle();

			// Carousels (https://www.embla-carousel.com/)
			initCarousels();
		}

		const initCarousels = () => {
			const $carousels = document.querySelectorAll( '.is-mobile-carousel' );
			if ( !$carousels.length ) return;

			const addDotBtnsAndClickHandlers = ( emblaApi, dotsNode ) => {
				let dotNodes = []

				const addDotBtnsWithClickHandlers = () => {
					dotsNode.innerHTML = emblaApi
						.scrollSnapList()
						.map( ( snapPositions, index ) => '<button class="embla__dot" type="button" aria-label="View slide ' + index + '"></button>' )
						.join( '' )

					dotNodes = Array.from( dotsNode.querySelectorAll( '.embla__dot' ) )
					dotNodes.forEach( ( dotNode, index ) => {
						dotNode.addEventListener( 'click', () => emblaApi.scrollTo( index ), false )
					} )
				}

				const toggleDotBtnsActive = () => {
					const previous = emblaApi.previousScrollSnap()
					const selected = emblaApi.selectedScrollSnap()
					dotNodes[previous].classList.remove( 'embla__dot--selected' )
					dotNodes[selected].classList.add( 'embla__dot--selected' )
				}

				emblaApi
					.on( 'init', addDotBtnsWithClickHandlers )
					.on( 'reInit', addDotBtnsWithClickHandlers )
					.on( 'init', toggleDotBtnsActive )
					.on( 'reInit', toggleDotBtnsActive )
					.on( 'select', toggleDotBtnsActive )

				return () => {
					dotsNode.innerHTML = ''
				}
			}

			$carousels.forEach( ( $carousel ) => {
				$carousel.insertAdjacentHTML( 'beforeend', '<div class="embla__dots"></div>' );

				const emblaApi = EmblaCarousel( $carousel, {
					breakpoints: {
						'(min-width: 1000.02px)': {active: false},
					},
				} )

				const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers( emblaApi, $carousel.querySelector( '.embla__dots' ) );
				emblaApi.on( 'destroy', removeDotBtnsAndClickHandlers )
			} )

		}

		const initCollapse = () => {
			// File#: _1_collapse
			// Usage: codyhouse.co/license
			(function () {
				let Collapse = function ( element ) {
					this.element = element;
					this.triggers = document.querySelectorAll( '[aria-controls="' + this.element.getAttribute( 'id' ) + '"]' );
					this.animate = this.element.getAttribute( 'data-collapse-animate' ) === 'on';
					this.animating = false;
					initCollapse( this );
				};

				function initCollapse( element ) {
					if ( element.triggers ) {
						// set initial 'aria-expanded' attribute for trigger elements
						updateTriggers( element, !Util.hasClass( element.element, 'is-hidden' ) );

						// detect click on trigger elements
						for ( let i = 0; i < element.triggers.length; i ++ ) {
							element.triggers[i].addEventListener( 'click', function ( event ) {
								event.preventDefault();
								toggleVisibility( element );
							} );
						}
					}

					// custom event
					element.element.addEventListener( 'collapseToggle', function ( event ) {
						toggleVisibility( element );
					} );
				}

				function toggleVisibility( element ) {
					let bool = Util.hasClass( element.element, 'is-hidden' );
					if ( element.animating ) return;
					element.animating = true;
					animateElement( element, bool );
					updateTriggers( element, bool );
				}

				function animateElement( element, bool ) {
					// bool === true -> show content
					if ( !element.animate || !window.requestAnimationFrame ) {
						Util.toggleClass( element.element, 'is-hidden', !bool );
						element.animating = false;
						return;
					}

					// animate content height
					Util.removeClass( element.element, 'is-hidden' );
					let initHeight = !bool ? element.element.offsetHeight : 0,
						finalHeight = !bool ? 0 : element.element.offsetHeight;

					Util.addClass( element.element, 'overflow-hidden' );

					Util.setHeight( initHeight, finalHeight, element.element, 200, function () {
						if ( !bool ) Util.addClass( element.element, 'is-hidden' );
						element.element.removeAttribute( "style" );
						Util.removeClass( element.element, 'overflow-hidden' );
						element.animating = false;
					}, 'easeInOutQuad' );
				}

				function updateTriggers( element, bool ) {
					for ( let i = 0; i < element.triggers.length; i ++ ) {
						bool ? element.triggers[i].setAttribute( 'aria-expanded', 'true' ) : element.triggers[i].removeAttribute( 'aria-expanded' );
					}
				}

				window.Collapse = Collapse;

				//initialize the Collapse objects
				let collapses = document.getElementsByClassName( 'js-collapse' );
				if ( collapses.length > 0 ) {
					for ( let i = 0; i < collapses.length; i ++ ) {
						new Collapse( collapses[i] );
					}
				}
			}());
		}

		const initButtonsToggle = () => {

			const throttle = ( fn, delay ) => {
				let timeout;
				return ( ...args ) => {
					if ( timeout ) {
						clearTimeout( timeout );
					}
					timeout = setTimeout( () => {
						timeout = null;
						fn( ...args );
					}, delay );
				};
			}

			const getToggleObject = ( $toggle ) => {
				const toggle = {
					$wrapper: null,
					$buttons: null,
					$expand: null,
					$collapse: null
				}

				const $buttonsId = $toggle.getAttribute( 'data-id' );
				if ( $buttonsId ) {
					toggle.$wrapper = document.getElementById( $buttonsId );
					toggle.$wrapper.classList.add( 'buttons-toggle-target' );
					if ( toggle.$wrapper ) {
						toggle.$buttons = toggle.$wrapper.querySelectorAll( '.wp-block-button' );
					}
				}

				toggle.$expand = $toggle.querySelector( '[data-action="expand"]' );
				toggle.$collapse = $toggle.querySelector( '[data-action="collapse"]' );

				return toggle;
			}

			const computeButtonRows = ( toggle ) => {
				let hasMultipleRows = false,
					firstRowHeight = 0,
					minOffset = Infinity;

				toggle.$wrapper.classList.remove( 'buttons-toggle-target' );

				const wrapperRect = toggle.$wrapper.getBoundingClientRect(),
					wrapperHeight = wrapperRect.height;
				toggle.$wrapper.classList.add( 'buttons-toggle-target' );

				// in mobile layout, the buttons are inline-blocks, in which case we let the first 4 buttons belong to the first "row"
				const buttonsPerRow = window.getComputedStyle( toggle.$buttons[0].firstElementChild ).display === 'inline-block'
					? 4
					: 1;

				toggle.$buttons.forEach( ( $button, i ) => {
					const buttonRect = $button.getBoundingClientRect();

					// set firstRowHeight from first button height
					if ( !firstRowHeight ) firstRowHeight = buttonRect.height * buttonsPerRow;

					// use top offset to check if this button is in the first row
					if ( buttonRect.top <= minOffset || i < buttonsPerRow ) {
						$button.dataset.isFirstRow = 'true';
						minOffset = buttonRect.top;
					} else {
						$button.dataset.isFirstRow = 'false';
						hasMultipleRows = true;
					}
				} );

				toggle.$wrapper.style.setProperty( '--full-height', wrapperHeight + 'px' );
				toggle.$wrapper.style.setProperty( '--row-height', firstRowHeight + 'px' );

				if ( !hasMultipleRows ) {
					toggle.$collapse.hidden = true;
					toggle.$expand.hidden = true;
				} else
					if ( hasMultipleRows && toggle.$expand.hidden === true && toggle.$collapse.hidden === true ) {
						toggle.$wrapper.dataset.showFirstRowOnly = 'true';
						toggle.$expand.hidden = false;
					}

				updateAria( toggle );
			}

			const clickToggle = ( isExpanding, toggle ) => {
				toggle.$expand.hidden = isExpanding;
				toggle.$collapse.hidden = !isExpanding;
				toggle.$wrapper.dataset.showFirstRowOnly = (!isExpanding).toString();
				updateAria( toggle );
			}

			const updateAria = ( toggle ) => {
				const collapsed = toggle.$wrapper.dataset.showFirstRowOnly;
				toggle.$buttons.forEach( $button => {
					if ( $button.dataset.isFirstRow === 'true' || collapsed === "false" ) {
						$button.setAttribute( 'aria-hidden', 'false' );
						$button.firstElementChild.removeAttribute( 'tabindex' );
					} else {
						$button.setAttribute( 'aria-hidden', 'true' );
						$button.firstElementChild.setAttribute( 'tabindex', '-1' );
					}
				} )
			}

			const $toggles = document.querySelectorAll( '.wp-block-acf-buttons-toggle' );

			$toggles.forEach( $toggle => {
				const toggle = getToggleObject( $toggle );
				if ( Object.values( toggle ).some( o => o === null ) ) return;

				computeButtonRows( toggle );

				const throttledCompute = throttle( computeButtonRows, 50 );
				window.addEventListener( 'resize', () => throttledCompute( toggle ) );

				toggle.$expand.addEventListener( 'click', () => clickToggle( true, toggle ) );
				toggle.$collapse.addEventListener( 'click', () => clickToggle( false, toggle ) );
			} );
		}

		return publicAPIs
	}
)()

LT_Theme.init()
